import React, { FC, ReactElement, ReactNode, useContext, useEffect, useMemo } from 'react'
import { Location, UIMatch, useLocation, useMatches } from 'react-router'
import { useHotkeys } from 'react-hotkeys-hook'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { LanguageContext, UserInfoContextProvider } from '../../React/Common/Contexts'
import { Router } from '../../../i18n'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { StringUtils } from '../../NonReact/utils/StringUtils'
import { Header } from '../Components/Molecules/Header/Header'
import { openHelp, setModalHelpTopicID } from '../../React/Common/Utils/HelpPortal'
import { RouteID } from '../Components/Molecules/Header/Types/Types'
import { DocTopic } from '../../React/Common/Constants/Documentation'
import { isStringNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { PlaceInAkixiNameSpace } from '../../NonReact/utils/Utils'
import { getQueryParam } from '../../NonReact/utils/LocationUtils'
import { $window } from '../../NonReact/common/AkixiWindow'
import { getSessionLanguage } from '../../React/Common/API/LanguageAPI'

type Props = {
    children: ReactNode
}

export const ROUTE_TO_DOC_TOPIC_MAP: Record<RouteID, DocTopic> = {
    [RouteID.AUTH]: DocTopic.SIGNINGIN,
    [RouteID.CHANGE_PASSWORD]: DocTopic.PASSWORDCHG,
    [RouteID.RESET_PASSWORD]: DocTopic.PASSWORDRESET,
    [RouteID.MFA]: DocTopic.SIGNINGIN,
    [RouteID.FORGOT_PASSWORD]: DocTopic.PASSWORDRESET,
    [RouteID.INSIGHTS]: DocTopic.CMD_INSIGHTS_HUB_V2,
    [RouteID.REPORTING_PORTAL]: DocTopic.REPORTING,
    [RouteID.PRESENCE]: DocTopic.CMD_TEAMS_PRESENCE,
    [RouteID.TERMS_AND_CONDITIONS]: DocTopic.TERMS_AND_CONDITIONS,
    [RouteID.TEAMS_AUTH]: DocTopic.SIGNINGIN,
    [RouteID.COOKIE_POLICY]: DocTopic.COOKIE_POLICY,
    [RouteID.PRIVACY_POLICY]: DocTopic.PRIVACY_POLICY,
    [RouteID.CHANGE_LANGUAGE]: DocTopic.SET_LANGUAGE,
    // TODO: Revisit documentation topic below
    [RouteID.CLIENT_CONFIGURATION]: DocTopic.ADMINCRMINTEGRATION,
    [RouteID.CRM_INTEGRATION]: DocTopic.ADMINCRMCONFIGURATION,
    [RouteID.CHANGE_USER_AGENT]: DocTopic.INTRODUCTION
}

export const RouterContainer: FC<Props> = ({ children }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(Router.NAME)

    const location: Location = useLocation()
    const matches: UIMatch[] = useMatches()

    const routeID: Definable<RouteID> = useMemo(
        (): Definable<RouteID> =>
            matches.find((match: UIMatch): boolean => match.pathname === location.pathname)?.id as Definable<RouteID>,
        [location, matches]
    )

    useHotkeys(
        'f1',
        (event: Event): void => {
            event.preventDefault()
            event.stopPropagation()

            openHelp()
        },
        {
            preventDefault: true,
            enabled: true,
            enableOnContentEditable: true,
            enableOnFormTags: true
        }
    )

    useEffect((): void => {
        if (location && matches && matches.length > 0) {
            document.title = language.get(`PageTitle_${routeID}`) ?? EMPTY_STRING

            document.body.classList.forEach((className: string): void => {
                if (className.includes('router-page-')) {
                    document.body.classList.remove(className)
                }
            })

            document.body.classList.add(
                `router-page-${StringUtils.convertCamelCaseToKebabCase(routeID)}` ?? EMPTY_STRING
            )
        }
    }, [location, matches])

    useEffect((): void => {
        if (isStringNotEmpty(ROUTE_TO_DOC_TOPIC_MAP?.[routeID ?? RouteID.REPORTING_PORTAL])) {
            setModalHelpTopicID(ROUTE_TO_DOC_TOPIC_MAP[routeID ?? RouteID.REPORTING_PORTAL])
        }

        PlaceInAkixiNameSpace('RouteID', routeID)
    }, [routeID])

    useEffect((): void => {
        const userAgentID: Nullable<string> = getQueryParam('UserAgentID')

        if (isStringNotEmpty(userAgentID)) {
            $window.AkixiUserAgentID = Number(userAgentID)
        }

        getSessionLanguage().then((lang: Nullable<string>): void => language.setLanguage(lang ?? 'en_GB'))
    }, [])

    return (
        <UserInfoContextProvider disableError={true}>
            <div className='router-content-container'>
                <Header routeID={routeID}>
                    <div className='main-page-content'>{children}</div>
                </Header>
            </div>
        </UserInfoContextProvider>
    )
}
